<template>
  <div class="event-teaser">
    <button
      v-if="teaserData"
      class="event-teaser__button"
      :disabled="!active"
      @click="openEventBooking"
    >
      <event-header
        has-event
        :active="active"
        :image="image"
        :title="title"
        :subtitle="subtitle"
        :tag-left="tagLeft"
        :tag-right="tagRight"
      />
    </button>
  </div>
</template>

<script>
import eventHeader from './eventHeader.vue'

import { BASE_URL, GAP_ALL_DAY } from '../settings.js'

import eventBus from '../eventBus'

export default {
  name: 'CEventTeaser',

  components: {
    eventHeader
  },

  props: {
    type: {
      type: String,
      required: true
    },
    date: {
      type: String,
      required: true
    },
    extended: Boolean,
    reservation: Boolean,
    spawner: {
      type: Boolean,
      default: false
    },
    position: {
      type: String,
      default: 'gaplist_top'
    }
  },

  data () {
    return {
      event: null,
      teaserData: null,
      spawned: false,

      displayed: false,
      trackable: false,
      tracked: false
    }
  },

  computed: {
    active () {
      return this.type === 'active'
    },
    image () {
      if (this.teaserData) {
        return `${BASE_URL}static/img/event_${this.teaserData.number}.jpg`
      }

      return false
    },
    title () {
      if (this.teaserData) {
        return this.translate(this.teaserData.title_de, this.teaserData.title_en)
      }

      return false
    },
    subtitle () {
      if (this.teaserData) {
        return this.translate(this.teaserData.subtitle_de, this.teaserData.subtitle_en)
      }

      return false
    },
    tagLeft () {
      if (this.teaserData) {
        return this.translate(this.teaserData.tag_left_de, this.teaserData.tag_left_en)
      }

      return false
    },
    tagRight () {
      if (this.teaserData) {
        return this.translate(this.teaserData.tag_right_de, this.teaserData.tag_right_en)
      }

      return false
    }
  },

  watch: {
    date () {
      this.fetchData()
    },
    event (newVal) {
      this.tracked = false
      this.updateEventTeaser(newVal)
    },
    teaserData () {
      if (
        'spawn' in this.$route.meta &&
        this.$route.meta.spawn === true &&
        'date' in this.$route.params &&
        this.date === this.$route.params.date &&
        !this.spawned &&
        this.active &&
        this.spawner &&
        this.event &&
        this.teaserData &&
        this.event.event_type !== '0' &&
        parseInt(this.teaserData.number) === parseInt(this.event.event_type)
      ) {
        // If a date is set in the route AND it matches this event's date, we spawn the booking
        this.openEventBooking()
        this.spawned = true
      }
    }
  },

  mounted () {
    // We immediately load the component if
    // `IntersectionObserver` is not supported.
    if (!('IntersectionObserver' in window)) {
      this.displayed = true
      this.track()
      return
    }

    const observer = new IntersectionObserver(entries => {
      // Use `intersectionRatio` because of Edge 15's
      // lack of support for `isIntersecting`.
      // See: https://github.com/w3c/IntersectionObserver/issues/211
      this.displayed = (entries[0].intersectionRatio > 0)
      if (this.displayed) {
        this.track()
      }
    })

    // We observe the root `$el` of the
    // mounted loading component to detect
    // when it becomes visible.
    observer.observe(this.$el)
  },

  created () {
    this.updateEventTeaser(null)
    this.fetchData()
  },

  methods: {
    translate (labelDe, labelEn) {
      if (this.$i18n.locale === 'de') {
        return labelDe
      } else {
        return labelEn
      }
    },

    async fetchData () {
      if (!this.date) {
        return
      }

      this.event = this.getEvent(this.date)

      if (this.event) {
        return
      }

      try {
        await this.$store.dispatch('getEvents', { date: this.date })
        this.event = this.getEvent(this.date)
      } catch (error) {
        eventBus.emit('handleError', error.response.data.error)
      }
    },

    getEvent (date) {
      if (!(date in this.$store.state.events)) {
        return null
      }

      if (!this.$store.state.events[date]) {
        return null
      }

      if (this.$store.state.events[date].length === 0) {
        return null
      }

      return this.$store.state.events[date][0]
    },

    makeTeaserProduct (eventNumber) {
      const list = 'Ticketapp Event-Teaser'
      let pos = 1 // Left
      if (this.type === 'active') { pos = 2 }
      if (this.type === 'next') { pos = 3 }

      const teaser = {
        name: this.teaserData.title_de,
        list: list,
        position: pos,
        date: this.date
      }

      if (eventNumber) {
        teaser.id = 'wt.e-' + eventNumber
      }

      return teaser
    },

    track () {
      if (!this.tracked && this.trackable && this.displayed && this.event) {
        const teaserProduct = this.makeTeaserProduct(this.event.event_type)
        this.$tracking.eetEventTeaserImpression(this.position, teaserProduct)
        this.tracked = true
      }
    },

    async updateEventTeaser (event) {
      let eventNumber = 0

      if (event !== null) {
        eventNumber = event.event_type
      }

      if (eventNumber >= 0) {
        try {
          await this.$store.dispatch('getEventTeaserData', eventNumber)

          if (eventNumber in this.$store.state.eventTeaser) {
            this.teaserData = this.$store.state.eventTeaser[eventNumber].data
          }

          // EET
          this.trackable = true
          this.track()
        } catch (error) {
          eventBus.emit('handleError', error.response.data.error)
        }
      }
    },

    async openEventBooking () {
      if (!this.event) {
        const gap = {
          gap: GAP_ALL_DAY,
          date: this.date
        }

        // Wait for the tour adding request to finish…
        await this.$store.dispatch('addTicketsToCart', { gap })

        // …then redirect to the tours booking page. This ensures the cart has
        // been successfully updated on the server before the next view
        // fetches the cart.
        this.$router.push({ name: `booking-tours-${this.$i18n.locale}` })

        return
      }

      // EET
      const teaserProduct = this.makeTeaserProduct(this.event.event_type)
      this.$tracking.eetEventTeaserClick(this.position, teaserProduct)

      const eventData = {
        image: this.image,
        title: this.title,
        subtitle: this.subtitle,
        tagRight: this.tagRight,
        event: this.event,
        date: this.date,
        reservation: this.reservation
      }

      eventBus.emit('showEvent', eventData)
    }
  }
}
</script>

<style lang="scss">
@import "../css/variables";

.event-teaser__button {
  display: block;
  width: 100%;
  padding: 0;

  font-family: $font-family;
  line-height: 1.4;

  border: none;
  background: none;

  cursor: pointer;
}

.event-teaser__extended {
  padding: $gutter-sm;

  background-color: #fff;
  border-bottom: $border-gap;

  @media (min-width: $breakpoint-md) {
    padding: ($gutter-sm * 1.5);
  }
}

.event-teaser__description {
  margin: 0;
}

.event-teaser__price-and-booking {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  margin: 1em 0 0;
}
</style>
